import React from 'react';
import { PageHeader, SectionHeader } from '../components/SectionHeader';
import { Clock, Trophy, Users, Globe, Star, Flag } from 'lucide-react';

const History = () => {
  const timeline = [
    { year: "1920", event: "Se funda la American Professional Football Association, precursora de la NFL." },
    { year: "1922", event: "La liga cambia su nombre a National Football League (NFL)." },
    { year: "1967", event: "Primer Super Bowl: Green Bay Packers vs Kansas City Chiefs." },
    { year: "1970", event: "Fusión AFL-NFL crea la estructura moderna de AFC y NFC." },
    { year: "2002", event: "La NFL se expande a 32 equipos con Houston Texans." },
    { year: "2007", event: "Primera temporada regular jugada fuera de EE.UU. (Londres)." },
    { year: "2019", event: "Primer partido oficial de NFL en México (Estadio Azteca)." },
    { year: "2025", event: "La NFL continúa su expansión global con partidos en Europa y más." }
  ];

  return (
    <div className="min-h-screen" data-testid="history-page">
      <PageHeader 
        title="Historia y Cultura"
        subtitle="El legado del deporte más popular de Estados Unidos"
        backgroundImage="https://images.unsplash.com/photo-1663563624897-de8972d7ce93?auto=format&fit=crop&w=2000&q=80"
      />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">
        
        {/* Brief History */}
        <section>
          <SectionHeader 
            title="Historia de la NFL"
            quarter="1Q"
            icon={Clock}
          />
          
          <div className="space-y-8">
            <p className="text-slate-300 leading-relaxed text-lg">
              La NFL es la <strong className="text-white">liga deportiva profesional más valiosa del mundo</strong>. 
              Lo que empezó como un grupo de equipos en pequeñas ciudades del medio oeste estadounidense se ha 
              convertido en un fenómeno cultural global con miles de millones de aficionados.
            </p>
            
            {/* Timeline */}
            <div className="space-y-4">
              {timeline.map((item, index) => (
                <div 
                  key={index}
                  className="flex gap-6 group"
                >
                  <div className="flex flex-col items-center">
                    <span 
                      className="text-lg font-bold text-emerald-400 w-16 text-center py-2 bg-slate-800 rounded-lg group-hover:bg-emerald-500/20 transition-colors"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      {item.year}
                    </span>
                    {index < timeline.length - 1 && (
                      <div className="w-0.5 h-full bg-slate-700 mt-2 min-h-[20px]" />
                    )}
                  </div>
                  <p className="text-slate-400 py-2">{item.event}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Super Bowl */}
        <section>
          <SectionHeader 
            title="El Super Bowl"
            quarter="2Q"
            icon={Trophy}
          />
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <p className="text-slate-300 leading-relaxed">
                El Super Bowl es la <strong className="text-emerald-400">final de la NFL</strong> y el evento 
                deportivo más visto en Estados Unidos. Se juega el primer domingo de febrero y enfrenta a 
                los campeones de la AFC y NFC.
              </p>
              
              <div className="tactical-card p-6 rounded-r-xl">
                <h4 
                  className="text-lg font-bold text-white mb-4 uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Más que un partido
                </h4>
                <ul className="space-y-3 text-slate-400 text-sm">
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-orange-400 mt-1 flex-shrink-0" />
                    <span>Show del medio tiempo con artistas de fama mundial</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-orange-400 mt-1 flex-shrink-0" />
                    <span>Anuncios publicitarios icónicos (millones por segundo)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-orange-400 mt-1 flex-shrink-0" />
                    <span>Fiestas y reuniones en todo el país</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-orange-400 mt-1 flex-shrink-0" />
                    <span>Audiencia global de más de 100 millones de personas</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="info-card flex flex-col justify-center">
              <div className="text-center">
                <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                <h4 
                  className="text-2xl font-bold text-white mb-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  TROFEO VINCE LOMBARDI
                </h4>
                <p className="text-slate-400 text-sm">
                  Nombrado en honor al legendario entrenador de los Packers. 
                  Fabricado en plata de ley por Tiffany & Co.
                </p>
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div>
                    <span className="text-3xl font-black text-emerald-400" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>7</span>
                    <span className="text-xs text-slate-500 block">Patriots/Steelers</span>
                  </div>
                  <div>
                    <span className="text-3xl font-black text-emerald-400" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>6</span>
                    <span className="text-xs text-slate-500 block">49ers</span>
                  </div>
                  <div>
                    <span className="text-3xl font-black text-emerald-400" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>5</span>
                    <span className="text-xs text-slate-500 block">Cowboys/Giants</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Fan Culture */}
        <section>
          <SectionHeader 
            title="Cultura de la Afición"
            quarter="3Q"
            icon={Users}
          />
          
          <div className="space-y-8">
            <p className="text-slate-300 leading-relaxed">
              Ser aficionado de la NFL es una <strong className="text-white">experiencia única</strong>. 
              Cada equipo tiene tradiciones, cánticos y rituales que se transmiten de generación en generación.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: "Tailgating",
                  description: "Fiestas en el parking antes del partido con barbacoas, bebidas y camaradería. Una tradición sagrada.",
                  icon: Flag,
                  color: "#f97316"
                },
                {
                  title: "Rivalidades",
                  description: "Cowboys vs Eagles, Packers vs Bears, Ravens vs Steelers. Enfrentamientos con décadas de historia.",
                  icon: Users,
                  color: "#ef4444"
                },
                {
                  title: "Tradiciones Locales",
                  description: "Desde el Terrible Towel de Pittsburgh hasta el Lambeau Leap de Green Bay. Cada equipo es único.",
                  icon: Star,
                  color: "#eab308"
                }
              ].map((item, index) => (
                <div key={index} className="tactical-card p-6 rounded-r-xl" style={{ borderLeftColor: item.color }}>
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${item.color}20` }}
                  >
                    <item.icon className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <h4 
                    className="text-lg font-bold text-white mb-2 uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {item.title}
                  </h4>
                  <p className="text-slate-400 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* International Expansion */}
        <section>
          <SectionHeader 
            title="Expansión Internacional"
            quarter="4Q"
            icon={Globe}
          />
          
          <div className="space-y-8">
            <p className="text-slate-300 leading-relaxed">
              La NFL ya no es solo americana. Con partidos regulares en <strong className="text-emerald-400">Londres, 
              Múnich, Frankfurt y Ciudad de México</strong>, la liga busca conquistar nuevos mercados. 
              En 2025 se jugó el primer partido en España, en el Santiago Bernabéu.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="info-card">
                <h4 
                  className="text-xl font-bold text-white mb-4 uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Partidos Internacionales
                </h4>
                <ul className="space-y-3">
                  {[
                    { city: "Londres", venue: "Wembley / Tottenham Hotspur Stadium", since: "2007" },
                    { city: "Ciudad de México", venue: "Estadio Azteca", since: "2016" },
                    { city: "Múnich", venue: "Allianz Arena", since: "2022" },
                    { city: "Frankfurt", venue: "Deutsche Bank Park", since: "2023" },
                    { city: "Madrid", venue: "Santiago Bernabéu", since: "2025" }
                  ].map((location, index) => (
                    <li key={index} className="flex justify-between items-center py-2 border-b border-slate-700 last:border-0">
                      <div>
                        <span className="text-white font-semibold">{location.city}</span>
                        <span className="text-slate-500 text-xs block">{location.venue}</span>
                      </div>
                      <span className="text-emerald-400 text-sm font-mono">Desde {location.since}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="info-card flex flex-col justify-center">
                <div className="text-center">
                  <Globe className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h4 
                    className="text-2xl font-bold text-white mb-4"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    NFL EN ESPAÑOL
                  </h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    La NFL tiene una fuerte presencia en el mercado hispanohablante con transmisiones 
                    en español, contenido dedicado y eventos especiales en México y España. 
                    ¡Somos millones de aficionados!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default History;
